import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import { getPaginatedCompaniesNames } from '@/services/ws-permissions/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { AsyncSelect } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import type { Option } from 'src/types';
import type { CompanyIdentifiersFragment } from './context';
import { useCompanySwitchContext } from './context';
import SelectContainer from './styles';

const CompaniesSwitch: React.FC = () => {
  const getPaginatedCompaniesNamesRequest = useLazyRequest({
    apiMethod: getPaginatedCompaniesNames,
  });

  const [t] = useTranslation(['backend', 'common', 'modules']);
  const { company, onCompanyChange } = useCompanySwitchContext();

  const loadOptions = (prefix: string) =>
    getPaginatedCompaniesNamesRequest.load({ filter: prefix }).then(({ data }) =>
      data.items.map(
        (item): Option<CompanyIdentifiersFragment> => ({
          label: item.name,
          value: {
            id: item.id,
            companyUUID: item.companyUUID,
            companyRole: item.companyRole,
            name: item.name,
          },
        }),
      ),
    );
  const pending = useSubscribe(getPaginatedCompaniesNamesRequest.pending$);

  const handleChange = useCallbackRef((option: SingleValue<Option<CompanyIdentifiersFragment>>) => {
    if (!company && !option) {
      return;
    }
    onCompanyChange(option?.value ?? null);
  });

  return (
    <SelectContainer data-testid='companies-switch'>
      <AsyncSelect<Option<CompanyIdentifiersFragment>>
        onChange={handleChange}
        placeholder={t('common:SELECT_COMPANY')}
        defaultOptions
        loadOptions={loadOptions}
        isClearable
        isLoading={pending}
        value={
          company
            ? {
                label: company.name,
                value: company,
              }
            : undefined
        }
      />
    </SelectContainer>
  );
};

export default CompaniesSwitch;
